// src/components/wizard/StepPost.jsx - ステップ3: SNS投稿

import React, { useState } from 'react';
import { COLORS, SPACING, BORDER_RADIUS, TRANSITIONS } from '../../styles/constants';

const StepPost = ({
  post,
  twitterConnected,
  threadsConnected,
  twitterUsername,
  threadsUsername,
  onPostTwitter,
  onPostThreads,
  onPostBoth,
  isPostingTwitter,
  isPostingThreads,
  onConnectTwitter,
  onConnectThreads,
  onStartOver,
  onBack,
  userPlan,
  onUpgrade,
  showToast
}) => {
  const [postedTo, setPostedTo] = useState({ twitter: false, threads: false });
  const [previewPlatform, setPreviewPlatform] = useState(twitterConnected ? 'twitter' : 'threads');
  const [copied, setCopied] = useState(false);

  const isPremium = userPlan === 'premium';

  const handlePost = async (platform) => {
    try {
      if (platform === 'twitter') {
        await onPostTwitter();
        setPostedTo(prev => ({ ...prev, twitter: true }));
      } else if (platform === 'threads') {
        await onPostThreads();
        setPostedTo(prev => ({ ...prev, threads: true }));
      } else if (platform === 'both') {
        await onPostBoth();
        setPostedTo({ twitter: true, threads: true });
      }
    } catch (err) {
      showToast('投稿に失敗しました', 'error');
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(post);
      setCopied(true);
      showToast('クリップボードにコピーしました', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      showToast('コピーに失敗しました', 'error');
    }
  };

  const isPosting = isPostingTwitter || isPostingThreads;
  const allPosted = (postedTo.twitter || !twitterConnected) && (postedTo.threads || !threadsConnected);

  const now = new Date();
  const timeString = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;

  return (
    <div style={{
      animation: 'fadeIn 0.3s ease-out'
    }}>
      {/* プレビューカード */}
      <div style={{
        background: COLORS.surface,
        borderRadius: BORDER_RADIUS.xl,
        padding: SPACING.lg,
        border: `1px solid ${COLORS.border}`,
        marginBottom: SPACING.md
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: SPACING.md
        }}>
          <h2 style={{
            fontSize: '1.125rem',
            fontWeight: '700',
            color: '#fff',
            margin: 0,
            marginBottom: SPACING.xs
          }}>
            投稿プレビュー
          </h2>
        </div>

        {/* プラットフォーム切り替え */}
        {twitterConnected && threadsConnected && (
          <div style={{
            display: 'flex',
            gap: '0.25rem',
            marginBottom: SPACING.md,
            background: COLORS.background,
            padding: '0.25rem',
            borderRadius: BORDER_RADIUS.md
          }}>
            <button
              onClick={() => setPreviewPlatform('twitter')}
              style={{
                flex: 1,
                padding: '0.5rem',
                background: previewPlatform === 'twitter' ? COLORS.surface : 'transparent',
                border: 'none',
                borderRadius: BORDER_RADIUS.sm,
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.8125rem',
                color: previewPlatform === 'twitter' ? '#fff' : '#64748b',
                transition: TRANSITIONS.default
              }}
            >
              𝕏 X
            </button>
            <button
              onClick={() => setPreviewPlatform('threads')}
              style={{
                flex: 1,
                padding: '0.5rem',
                background: previewPlatform === 'threads' ? COLORS.surface : 'transparent',
                border: 'none',
                borderRadius: BORDER_RADIUS.sm,
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.8125rem',
                color: previewPlatform === 'threads' ? '#fff' : '#64748b',
                transition: TRANSITIONS.default
              }}
            >
              @ Threads
            </button>
          </div>
        )}

        {/* SNSプレビュー */}
        <div style={{
          background: COLORS.background,
          borderRadius: BORDER_RADIUS.md,
          padding: SPACING.md,
          border: `1px solid ${COLORS.border}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: COLORS.primary,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: '700',
              fontSize: '1rem',
              flexShrink: 0
            }}>
              {previewPlatform === 'twitter'
                ? (twitterUsername?.[0]?.toUpperCase() || 'U')
                : (threadsUsername?.[0]?.toUpperCase() || 'U')}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.25rem'
              }}>
                <span style={{
                  fontWeight: '700',
                  fontSize: '0.9375rem',
                  color: '#fff'
                }}>
                  {previewPlatform === 'twitter' ? twitterUsername : threadsUsername || 'あなた'}
                </span>
                <span style={{
                  fontSize: '0.8125rem',
                  color: '#64748b'
                }}>
                  · {timeString}
                </span>
              </div>
              <div style={{
                fontSize: '0.9375rem',
                lineHeight: '1.6',
                color: '#e2e8f0',
                whiteSpace: 'pre-wrap'
              }}>
                {post}
              </div>
            </div>
          </div>
        </div>

        {/* コピーボタン（常に表示） */}
        <button
          onClick={handleCopy}
          style={{
            width: '100%',
            marginTop: SPACING.md,
            padding: '0.875rem',
            background: copied ? COLORS.success : COLORS.primary,
            color: '#fff',
            border: 'none',
            borderRadius: BORDER_RADIUS.md,
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: SPACING.sm
          }}
        >
          {copied ? '✓ コピーしました' : '📋 テキストをコピー'}
        </button>
      </div>

      {/* プレミアムユーザー向け: SNS投稿アクション */}
      {isPremium ? (
        <div style={{
          background: COLORS.surface,
          borderRadius: BORDER_RADIUS.xl,
          padding: SPACING.lg,
          border: `1px solid ${COLORS.border}`
        }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '700',
            color: '#fff',
            margin: 0,
            marginBottom: SPACING.md,
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: SPACING.sm
          }}>
            <span>👑</span>
            SNSに直接投稿
          </h3>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: SPACING.sm
          }}>
            {/* Twitter投稿ボタン */}
            {twitterConnected ? (
              <button
                onClick={() => handlePost('twitter')}
                disabled={isPosting || postedTo.twitter}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: postedTo.twitter
                    ? COLORS.success
                    : '#e2e8f0',
                  color: postedTo.twitter ? '#fff' : '#0a0f1a',
                  border: 'none',
                  borderRadius: BORDER_RADIUS.md,
                  cursor: postedTo.twitter || isPosting ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  transition: TRANSITIONS.default,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: SPACING.sm,
                  opacity: isPosting && !isPostingTwitter ? 0.7 : 1
                }}
              >
                {isPostingTwitter ? (
                  <>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid rgba(0,0,0,0.2)',
                      borderTopColor: '#0a0f1a',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    投稿中...
                  </>
                ) : postedTo.twitter ? (
                  <>✓ Xに投稿しました</>
                ) : (
                  <>
                    <span style={{ fontWeight: '800' }}>𝕏</span>
                    Xに投稿
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={onConnectTwitter}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: 'transparent',
                  color: '#94a3b8',
                  border: `1px dashed rgba(255,255,255,0.2)`,
                  borderRadius: BORDER_RADIUS.md,
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: SPACING.sm
                }}
              >
                <span style={{ fontWeight: '800' }}>𝕏</span>
                Xアカウントを連携
              </button>
            )}

            {/* Threads投稿ボタン */}
            {threadsConnected ? (
              <button
                onClick={() => handlePost('threads')}
                disabled={isPosting || postedTo.threads}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: postedTo.threads
                    ? COLORS.success
                    : '#cbd5e1',
                  color: postedTo.threads ? '#fff' : '#0a0f1a',
                  border: 'none',
                  borderRadius: BORDER_RADIUS.md,
                  cursor: postedTo.threads || isPosting ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  transition: TRANSITIONS.default,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: SPACING.sm,
                  opacity: isPosting && !isPostingThreads ? 0.7 : 1
                }}
              >
                {isPostingThreads ? (
                  <>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid rgba(0,0,0,0.2)',
                      borderTopColor: '#0a0f1a',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    投稿中...
                  </>
                ) : postedTo.threads ? (
                  <>✓ Threadsに投稿しました</>
                ) : (
                  <>
                    <span style={{ fontWeight: '800' }}>@</span>
                    Threadsに投稿
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={onConnectThreads}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: 'transparent',
                  color: '#94a3b8',
                  border: `1px dashed rgba(255,255,255,0.2)`,
                  borderRadius: BORDER_RADIUS.md,
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: SPACING.sm
                }}
              >
                <span style={{ fontWeight: '800' }}>@</span>
                Threadsアカウントを連携
              </button>
            )}

            {/* 両方に投稿 */}
            {twitterConnected && threadsConnected && !allPosted && (
              <button
                onClick={() => handlePost('both')}
                disabled={isPosting}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: COLORS.primary,
                  color: '#fff',
                  border: 'none',
                  borderRadius: BORDER_RADIUS.md,
                  cursor: isPosting ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  transition: TRANSITIONS.default,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: SPACING.sm,
                  marginTop: SPACING.xs
                }}
              >
                ✨ 両方に投稿
              </button>
            )}
          </div>

          {/* 完了時のメッセージ */}
          {allPosted && (twitterConnected || threadsConnected) && (
            <div style={{
              marginTop: SPACING.lg,
              padding: SPACING.md,
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: BORDER_RADIUS.md,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: SPACING.xs }}>🎉</div>
              <p style={{
                fontSize: '0.9375rem',
                fontWeight: '600',
                color: COLORS.success,
                margin: 0
              }}>
                投稿が完了しました！
              </p>
            </div>
          )}
        </div>
      ) : (
        /* 無料ユーザー向け: アップグレード促進 */
        <div style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(16, 185, 129, 0.1))',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: BORDER_RADIUS.xl,
          padding: SPACING.xl,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: SPACING.md }}>👑</div>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: '700',
            margin: 0,
            marginBottom: SPACING.sm,
            color: '#fff'
          }}>
            SNS自動投稿を使いたい？
          </h3>
          <p style={{
            fontSize: '0.9375rem',
            color: '#94a3b8',
            marginBottom: SPACING.lg,
            lineHeight: '1.6'
          }}>
            プレミアムプランなら、生成した投稿を
            <br />
            X・Threadsにワンクリックで投稿できます
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: SPACING.sm,
            marginBottom: SPACING.lg,
            fontSize: '0.8125rem'
          }}>
            <div style={{ padding: SPACING.sm, background: COLORS.surface, borderRadius: BORDER_RADIUS.md, color: '#e2e8f0' }}>
              ⚡ 無制限生成
            </div>
            <div style={{ padding: SPACING.sm, background: COLORS.surface, borderRadius: BORDER_RADIUS.md, color: '#e2e8f0' }}>
              𝕏 X自動投稿
            </div>
            <div style={{ padding: SPACING.sm, background: COLORS.surface, borderRadius: BORDER_RADIUS.md, color: '#e2e8f0' }}>
              @ Threads投稿
            </div>
            <div style={{ padding: SPACING.sm, background: COLORS.surface, borderRadius: BORDER_RADIUS.md, color: '#e2e8f0' }}>
              🔄 同時投稿
            </div>
          </div>

          <button
            onClick={onUpgrade}
            style={{
              width: '100%',
              padding: '1rem',
              background: COLORS.primary,
              color: '#fff',
              border: 'none',
              borderRadius: BORDER_RADIUS.md,
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '700'
            }}
          >
            プレミアムプランを見る（¥980/月）
          </button>

          <p style={{
            marginTop: SPACING.md,
            fontSize: '0.75rem',
            color: '#64748b'
          }}>
            ※ 今はテキストをコピーして手動投稿できます
          </p>
        </div>
      )}

      {/* ナビゲーション */}
      <div style={{
        display: 'flex',
        gap: SPACING.sm,
        marginTop: SPACING.md
      }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: '0.875rem',
            background: 'transparent',
            color: '#94a3b8',
            border: `1px solid ${COLORS.border}`,
            borderRadius: BORDER_RADIUS.md,
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}
        >
          ← 戻る
        </button>
        <button
          onClick={onStartOver}
          style={{
            flex: 1,
            padding: '0.875rem',
            background: (isPremium && allPosted) ? COLORS.primary : 'transparent',
            color: (isPremium && allPosted) ? '#fff' : '#94a3b8',
            border: (isPremium && allPosted) ? 'none' : `1px solid ${COLORS.border}`,
            borderRadius: BORDER_RADIUS.md,
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}
        >
          新しく作成
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default StepPost;
